<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SquareArrowRightUpIcon, TrashBinTrashIcon } from '@solar-icons/vue/linear'
import AppBadge from '../../primitives/AppBadge/AppBadge.vue'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppSeparator from '../../primitives/AppSeparator/AppSeparator.vue'
import AppPageSection from '../../shell/AppPage/AppPageSection.vue'
import ArticleResultPreview from '../../article/ArticleResultPreview.vue'
import { todoItems } from '../../article/article-presenter'
import { BELIEF_PHASE_ACCENT } from '../../brand/BeliefMapBoard/belief-phase'
import { PHASE_LABEL_KEYS } from '../../../lib/strategy/journey-phase'
import { STATUS_BADGE } from '../../../lib/content/content-status'
import type { JourneyPhase } from '../../../types/brand'
import type { ArticleDoc, ContentItem } from '../../../types/content'
import { useFormatters } from '../../../composables/useFormatters'

const TODO_SHOWN = 3

const { t } = useI18n()
const { formatDate, formatDecimal } = useFormatters()

const props = withDefaults(
  defineProps<{
    /** The article's content item. */
    item: ContentItem
    /** The article's document; `null` while Raldo has not planned it yet. */
    article?: ArticleDoc | null
    /** The journey stage the article serves, when known. */
    phase?: JourneyPhase | null
    /** The question of the page the article answers. */
    question?: string | null
    /** The article's topic name, when it has one. */
    topicName?: string | null
    /** Whether the delete action is offered. */
    canDelete?: boolean
    /** Whether the delete is in flight; drives the delete button's `loading`. */
    removing?: boolean
  }>(),
  {
    article: null,
    phase: null,
    question: null,
    topicName: null,
    canDelete: false,
    removing: false,
  },
)

const emit = defineEmits<{
  /** Opens the article workbench. */
  openArticle: []
  remove: []
}>()

const status = computed(() => STATUS_BADGE[props.item.status])

const title = computed(() => props.article?.plan.title || props.item.title)

const stats = computed(() => {
  const words = props.article?.wordCount ?? 0
  if (!props.article || words <= 0) return null
  const sections = props.article.sections.length
  return t('content.calendar.articleStats', {
    words: t('content.calendar.articleWords', { count: formatDecimal(words, 0) }, words),
    sections: t(
      'content.calendar.articleSections',
      { count: formatDecimal(sections, 0) },
      sections,
    ),
  })
})

const todo = computed(() => (props.article ? todoItems(props.article.checks) : []))

const todoShown = computed(() => todo.value.slice(0, TODO_SHOWN))

const todoRest = computed(() => todo.value.length - todoShown.value.length)

const openSectionIds = computed(
  () => new Set(todo.value.flatMap((entry) => (entry.sectionId ? [entry.sectionId] : []))),
)

const showsResult = computed(() =>
  Boolean(props.article && (props.article.plan.title || props.article.plan.metaDescription)),
)
</script>

<template>
  <div class="flex flex-col gap-12">
    <header class="flex min-w-0 flex-col gap-6">
      <div class="flex min-w-0 flex-col gap-2">
        <h3 class="text-h3-medium text-text-primary">{{ title }}</h3>
        <p
          v-if="question || topicName"
          class="flex min-w-0 flex-wrap gap-x-8 text-caption text-text-tertiary"
        >
          <span v-if="question">{{ t('content.calendar.articleQuestion', { question }) }}</span>
          <span v-if="topicName">{{
            t('content.calendar.articleTopic', { name: topicName })
          }}</span>
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <AppBadge :tone="status.tone">{{ t(status.labelKey) }}</AppBadge>
        <AppBadge v-if="phase" :tone="BELIEF_PHASE_ACCENT[phase]">
          {{ t(PHASE_LABEL_KEYS[phase]) }}
        </AppBadge>
      </div>
    </header>

    <AppPageSection :title="t('calendar.dock.articleState')" heading-level="h3">
      <p v-if="!article" class="text-body-sm text-text-secondary">
        {{ t('calendar.dock.planning') }}
      </p>

      <div v-else class="flex flex-col gap-8">
        <div class="flex flex-col gap-2">
          <p
            class="text-body-sm"
            :class="article.checks ? 'text-text-primary' : 'text-text-secondary'"
          >
            {{ article.checks ? t('calendar.dock.checked') : t('calendar.dock.writing') }}
          </p>
          <p v-if="stats" class="text-caption text-text-tertiary">{{ stats }}</p>
        </div>

        <div v-if="todo.length" class="flex flex-col gap-2">
          <p class="text-caption text-text-tertiary">
            {{ t('calendar.dock.todo', { count: todo.length }, todo.length) }}
          </p>
          <ul class="flex list-none flex-col gap-2">
            <li v-for="entry in todoShown" :key="entry.id" class="text-body-sm text-text-primary">
              {{ entry.title }}
            </li>
          </ul>
          <p v-if="todoRest > 0" class="text-caption text-text-tertiary">
            {{ t('calendar.dock.more', { count: todoRest }) }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <a
            v-if="article.publishedUrl"
            :href="article.publishedUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="icon-sm flex w-fit items-center gap-2 text-caption-medium text-accent-blue hover:underline"
          >
            {{ t('calendar.dock.openPublished') }}
            <SquareArrowRightUpIcon class="shrink-0" />
          </a>
          <p v-else class="text-caption text-text-tertiary">{{ t('calendar.dock.goesLive') }}</p>
          <p
            v-if="article.publishedUrl && article.verifiedAt"
            class="text-caption text-text-tertiary"
          >
            {{
              t('calendar.dock.verified', { date: formatDate(article.verifiedAt, 'monthDayYear') })
            }}
          </p>
        </div>
      </div>
    </AppPageSection>

    <AppPageSection
      v-if="article && article.sections.length > 0"
      :title="t('calendar.dock.outline')"
      heading-level="h3"
    >
      <ol class="grid list-none grid-cols-numbered gap-x-6 gap-y-4">
        <li
          v-for="(section, index) in article.sections"
          :key="section.id"
          class="col-span-full grid grid-cols-subgrid items-baseline"
        >
          <span class="text-end text-caption tabular-nums text-text-tertiary" aria-hidden="true">
            {{ formatDecimal(index + 1, 0) }}
          </span>
          <span class="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <span class="min-w-0 text-body-sm text-text-primary">{{ section.heading }}</span>
            <AppBadge v-if="openSectionIds.has(section.id)" tone="quiet">
              {{ t('article.details.todo') }}
            </AppBadge>
          </span>
        </li>
      </ol>
    </AppPageSection>

    <AppPageSection
      v-if="article && showsResult"
      :title="t('article.preview.heading')"
      heading-level="h3"
    >
      <ArticleResultPreview
        readonly
        :slug="article.handoff?.slug ?? ''"
        :title-tag="article.plan.title"
        :meta-description="article.plan.metaDescription"
      />
    </AppPageSection>

    <AppSeparator />

    <div class="flex flex-wrap items-center gap-4">
      <AppButton
        variant="primary"
        :label="t('content.calendar.articleOpen')"
        @click="emit('openArticle')"
      />
      <div v-if="canDelete" class="ml-auto flex items-center gap-2">
        <AppButton
          icon-only
          :start-icon="TrashBinTrashIcon"
          variant="ghost"
          :loading="removing"
          :aria-label="
            t('content.actionForTitleAria', { action: t('common.delete'), title: item.title })
          "
          :title="t('common.delete')"
          @click="emit('remove')"
        />
      </div>
    </div>
  </div>
</template>
